import { useState, useCallback } from "react"
import { trpc } from "#/lib/trpc/client"
import { dispatchSessionExpiredIfUnauthorized } from "#/utils/queryErrorHandler"

// Legacy Google Places JS API shape. The V1 → legacy projection lives on
// the BFF; downstream FE consumers depend on this exact field layout.
export interface PlacePrediction {
  place_id: string
  description: string
  matched_substrings: Array<{ offset: number; length: number }>
  structured_formatting: {
    main_text: string
    main_text_matched_substrings: Array<{ offset: number; length: number }>
    secondary_text: string
  }
  terms: Array<{ offset: number; value: string }>
  types: string[]
}

interface UsePlacesAutocompleteResult {
  placePredictions: PlacePrediction[]
  isPlacePredictionsLoading: boolean
  getPlacePredictions: (input: string) => Promise<void>
}

export function usePlacesAutocomplete(language: string): UsePlacesAutocompleteResult {
  const [placePredictions, setPlacePredictions] = useState<PlacePrediction[]>([])
  const [isPlacePredictionsLoading, setIsPlacePredictionsLoading] = useState(false)

  const getPlacePredictions = useCallback(
    async (input: string) => {
      if (!input) {
        setPlacePredictions([])
        return
      }

      setIsPlacePredictionsLoading(true)
      try {
        const predictions = await trpc.location.placesAutocomplete.query({
          input,
          languageCode: language
        })
        setPlacePredictions(predictions)
      } catch (error) {
        // This call sits outside react-query, so the QueryCache onError
        // bridge doesn't fire. Route 401s to the same SESSION_EXPIRED
        // dispatch before swallowing so a stale session in the location
        // picker doesn't silently show "no results".
        dispatchSessionExpiredIfUnauthorized(error)
        // Non-401 failures are non-fatal — geocoding catches address typos
        // at submission.
        setPlacePredictions([])
      } finally {
        setIsPlacePredictionsLoading(false)
      }
    },
    [language]
  )

  return { placePredictions, isPlacePredictionsLoading, getPlacePredictions }
}
